import clsx from 'clsx'
import { type StyleValue, computed, defineComponent } from 'vue'

import VIcon from './VIcon'

export interface Props {
  id?: string
  tData?: string
  class?: string
  size?: 'sm' | 'normal'
  color?: 'primary' | 'gray'
  value: boolean
  indeterminateEnabled?: boolean
  disabled?: boolean
  onInput?: (value: boolean, payload: Event) => void
  onClick?: (payload: Event) => void
}

export default defineComponent<Props>((props) => {
  // #region Props Getters

  const tData = computed((): string => props.tData ?? 'v-checkbox')
  const size = computed((): 'sm' | 'normal' => props.size ?? 'normal')
  const color = computed((): 'primary' | 'gray' => props.color ?? 'primary')
  const indeterminateEnabled = computed((): boolean => props.indeterminateEnabled ?? false)

  // #endregion

  // #region Getters

  const checkedIcon = computed((): string => {
    if (!props.value) {
      return ''
    }

    return indeterminateEnabled.value ? 'material-symbols:indeterminate-check-box' : 'material-symbols:check-box'
  })

  const inputSizeClass = computed((): string => {
    return size.value === 'normal' ? clsx('size-10.5') : clsx('size-9.5')
  })

  const inputHoverClass = computed((): string => {
    return color.value === 'primary'
      ? clsx({ 'hover:bg-primary-50': !props.disabled })
      : clsx({ 'hover:bg-neutral-100': !props.disabled })
  })

  const iconSizeClass = computed((): StyleValue => {
    return size.value === 'normal' ? clsx('text-[24px]') : clsx('text-[20px]')
  })

  const checkedColor = computed((): string => {
    return color.value === 'primary'
      ? clsx('text-primary')
      : clsx('text-neutral-500')
  })

  // #endregion

  return () => (
    <div
      class={clsx(
        'pointer-coarse:hover:bg-transparent relative rounded-full',
        props.class,
        inputSizeClass.value,
        inputHoverClass.value
      )}
    >
      <input
        t-data={tData.value}
        id={props.id}
        class={clsx(
          'peer appearance-none',
          inputSizeClass.value,
          {
            'cursor-pointer': !props.disabled,
            'cursor-not-allowed': props.disabled
          }
        )}
        type='checkbox'
        disabled={props.disabled}
        checked={props.value}
        onInput={(e) => {
          const checked = (e.target as HTMLInputElement).checked
          props.onInput?.(checked, e)
        }}
        onClick={props.onClick}
      />
      {props.value &&
        <VIcon
          class={clsx(
            'pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
            checkedColor.value,
            { '!text-neutral-400': props.disabled },
            iconSizeClass.value)}
          icon={checkedIcon.value}
        />}
      {!props.value &&
        <VIcon
          class={clsx(
            'pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
            { '!text-neutral-400': props.disabled },
            iconSizeClass.value
          )}
          icon='material-symbols:check-box-outline-blank'
        />}
    </div>
  )
}, {
  name: 'VCheckbox',
  props: ['id', 'class', 'tData', 'size', 'color', 'value', 'indeterminateEnabled', 'disabled', 'onInput', 'onClick']
})
